import React, { useMemo, useState, useEffect } from "react";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stack,
  Divider,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from "@mui/material";

import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import ShoppingCartRoundedIcon from "@mui/icons-material/ShoppingCartRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import RoomRoundedIcon from "@mui/icons-material/RoomRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";

import { TextField, CircularProgress } from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { useRequests } from "../../hooks/useRequests";
import { fetchData } from "../../api/client";
import { isCardPaymentTipo } from "../../constants/paymentCardBrands";
import CardTagsRow, { parseTagsCsv } from "./CardTagsRow";
import { formatClientDisplayPhone } from "../../utils/phone";


const money = (v) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : v ?? "—";


const SectionTitle = ({ icon, title, extra }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
    <Box sx={{ color: "text.secondary", "& svg": { fontSize: 20 } }}>{icon}</Box>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    <Box sx={{ flex: 1 }} />
    {extra}
  </Stack>
);

const OrderDetailsDialog = ({ open, onClose, card }) => {
  const addressLine = useMemo(() => {
    const a = card?.address;
    if (!a) return "";
    const s = a.street || "";
    const tail =
      a.city && a.uf ? `${a.city}, ${a.uf}` : a.city || a.uf || "";
    return [s, tail].filter(Boolean).join(" — ");
  }, [card]);

  const copyCode = () => {
    if (card?.orderCode) {
      navigator.clipboard?.writeText(String(card.orderCode));
    }
  };

  const items = card?.items || [];
  const subtotal = Number(card?.subtotal || 0);
  const freight = Number(card?.shippingCost || 0);
  const total = Number(card?.total || 0);
  const paymentAdj = useMemo(() => {
    const v = total - subtotal - freight;
    return v > 0.009 ? v : 0;
  }, [total, subtotal, freight]);


  const client = card?.client;
  const hasTags =
    parseTagsCsv(client?.tags).length > 0 || parseTagsCsv(card?.tags).length > 0;

  const { updateMutation } = useRequests();

  const [editingField, setEditingField] = useState(null);
  const [leadFields, setLeadFields] = useState({
    nf: card?.nf ?? "",
    nsu: card?.nsu ?? "",
  });

  const [loadingFields, setLoadingFields] = useState({});
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [localCancellation, setLocalCancellation] = useState(card?.cancellation || null);

  useEffect(() => {
    setLeadFields({
      nf: card?.nf ?? "",
      nsu: card?.nsu ?? "",
    });
    setLocalCancellation(card?.cancellation || null);
    setConfirmCancel(false);
    setCancelError(null);
  }, [card]);

  const canCancel = Boolean(localCancellation?.canRequest);
  const alreadyCancelled = Boolean(localCancellation?.alreadyCancelled);

  const handleCancelOrder = async () => {
    if (!card?.id) return;
    setCanceling(true);
    setCancelError(null);
    try {
      const res = await fetchData(`/sales-pipeline/leads/${card.id}/cancel`, "POST", {});
      const cancellation = res?.cancellation || res?.data?.cancellation;
      setLocalCancellation(
        cancellation || {
          enabled: true,
          canRequest: false,
          alreadyCancelled: true,
        }
      );
      setConfirmCancel(false);
      window.dispatchEvent(new CustomEvent("xpro:sales-pipeline-refresh"));
    } catch (err) {
      setCancelError(err?.message || "Não foi possível cancelar o pedido.");
    } finally {
      setCanceling(false);
    }
  };

  const apiFieldMap = {
    nf: "nf",
    nsu: "nsu",
  };

  const handleLeadFieldChange = (field, value) => {
    if (!card?.id) return;

    setLoadingFields((prev) => ({ ...prev, [field]: true }));

    const apiField = apiFieldMap[field] ?? field;

    updateMutation.mutate(
      {
        id: card.id,
        [apiField]: value,
      },
      {
        onSuccess: () => {
          setLoadingFields((prev) => ({ ...prev, [field]: false }));
          setEditingField(null);
        },
        onError: () => {
          setLoadingFields((prev) => ({ ...prev, [field]: false }));
        },
      }
    );
  };

  const renderEditableLeadField = (label, field, options = {}) => {
    const { disabled = false, disabledTooltip } = options;
    const isEditing = editingField === field;
    const isLoading = loadingFields[field];

    if (disabled && !isEditing) {
      return (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {label}: <strong>{leadFields[field] || "—"}</strong>
          </Typography>
          {disabledTooltip && (
            <Tooltip title={disabledTooltip}>
              <Chip size="small" label="Automático" variant="outlined" />
            </Tooltip>
          )}
        </Stack>
      );
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {!isEditing ? (
          <>
            <Typography variant="body2" color="text.secondary">
              {label}: <strong>{leadFields[field] || "—"}</strong>
            </Typography>

            <IconButton
              size="small"
              onClick={() => {
                if (!disabled) {
                  setEditingField(field);
                }
              }}
            >
              <EditRoundedIcon fontSize="inherit" />
            </IconButton>
          </>
        ) : (
          <>
            <TextField
              size="small"
              label={label}
              value={leadFields[field]}
              onChange={(e) =>
                setLeadFields((prev) => ({
                  ...prev,
                  [field]: e.target.value,
                }))
              }
            />

            {isLoading ? (
              <CircularProgress size={18} />
            ) : (
              <>
                <IconButton
                  size="small"
                  color="success"
                  onClick={() =>
                    handleLeadFieldChange(field, leadFields[field])
                  }
                >
                  <CheckRoundedIcon fontSize="inherit" />
                </IconButton>

                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setEditingField(null)}
                >
                  <CloseRoundedIcon fontSize="inherit" />
                </IconButton>
              </>
            )}
          </>
        )}
      </Stack>
    );
  };



  // console.log("Rendering OrderDetailsDialog", card.id, card);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      {/* HEADER */}
      <DialogTitle sx={{ pr: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box>
            <Typography variant="h6" sx={{ lineHeight: 1 }}>
              {card?.companyName || "Pedido"}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              {!!card?.orderCode && (
                <Chip size="small" label={`#${card.orderCode}`} />
              )}
              {alreadyCancelled && (
                <Chip size="small" color="error" label="Cancelamento solicitado" />
              )}
              {!!card?.channelType && (
                <Chip size="small" label={card.channelType} variant="outlined" />
              )}
              {!!card?.createdAtLabel && (
                <Typography variant="caption" color="text.secondary">
                  {card.createdAtLabel}
                </Typography>
              )}
            </Stack>
          </Box>

          <Box sx={{ flex: 1 }} />

          {/* RESUMO FINANCEIRO */}
          <Stack direction="row" spacing={1}>
            <Chip size="small" label={`Subtotal: ${money(subtotal)}`} />
            <Chip size="small" label={`Frete: ${money(freight)}`} />

            {paymentAdj > 0 && (
              <Chip
                size="small"
                color="warning"
                label={`Acréscimo: ${money(paymentAdj)}`}
              />
            )}

            <Chip
              size="small"
              color="primary"
              label={`Total: ${money(total)}`}
            />
          </Stack>


          <Tooltip title="Copiar código do pedido">
            <IconButton onClick={copyCode}>
              <ContentCopyRoundedIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Imprimir">
            <IconButton onClick={() => window.print()}>
              <PrintRoundedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {(!!client || hasTags) && (
          <>
            {!!client && (
              <>
                <SectionTitle
                  icon={<PersonRoundedIcon />}
                  title="Cliente"
                  extra={
                    client.id && (
                      <Button
                        size="small"
                        variant="outlined"
                        href={`/clients/${client.id}`}
                        target="_blank"
                      >
                        Ver cadastro completo
                      </Button>
                    )
                  }
                />

                <Typography variant="body2">
                  <strong>{client.name}</strong>{" "}
                  <Chip
                    size="small"
                    sx={{ ml: 1 }}
                    label={client.type === "PJ" ? "Pessoa Jurídica" : "Pessoa Física"}
                    variant="outlined"
                  />
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {client.type === "PJ" ? "CNPJ" : "CPF"}:{" "}
                  {client.document || "—"}
                </Typography>

                {(() => {
                  const phoneLabel = formatClientDisplayPhone(client);
                  if (!phoneLabel) return null;
                  return (
                  <Typography variant="body2" color="text.secondary">
                    Telefone: {phoneLabel}
                  </Typography>
                  );
                })()}

                {client.email && (
                  <Typography variant="body2" color="text.secondary">
                    Email: {client.email}
                  </Typography>
                )}
              </>
            )}

            {hasTags && (
              <CardTagsRow
                clientTags={client?.tags}
                requestTags={card?.tags}
                sx={{ mt: client ? 1 : 0, mb: 0 }}
              />
            )}

            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* ITENS */}
        <SectionTitle
          icon={<ShoppingCartRoundedIcon />}
          title="Itens do pedido"
        />

        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell>Qtd</TableCell>
              <TableCell align="right">Vlr unit.</TableCell>
              <TableCell align="right">Subtotal</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((it, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  {it.name}
                  {it.sku && (
                    <div style={{ fontSize: 12, color: "#666" }}>
                      SKU: {it.sku}
                    </div>
                  )}
                </TableCell>
                <TableCell>{it.qty}</TableCell>
                <TableCell align="right">{money(it.price)}</TableCell>
                <TableCell align="right">
                  {money(it.qty * it.price)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />

        {/* PAGAMENTO */}
        {!!card?.payment && (
          <>
            <SectionTitle
              icon={<PaymentsRoundedIcon />}
              title="Pagamento"
            />

            <Typography variant="body2">
              Método:{" "}
              <strong>{card.payment.methodLabel || "—"}</strong>
            </Typography>

            {card.payment.transactionId && (
              <Typography variant="body2" color="text.secondary">
                Transação: {card.payment.transactionId}
              </Typography>
            )}

            {!!card?.id && (
              <>
                {renderEditableLeadField("NF", "nf")}
              </>
            )}


            {/* DETALHES CARTÃO DE CRÉDITO */}
            {(isCardPaymentTipo(card.payment.methodTipo) ||
              card.payment.cardBrand ||
              Number.isFinite(card.payment.installments)) && (
              <>
                {!!card?.id && (
                  <>
                    {renderEditableLeadField("NSU", "nsu", {
                      disabled: !!card?.nsu,
                      disabledTooltip: "NSU preenchido automaticamente pela operadora de cartão.",
                    })}
                  </>
                )}
                {card.payment.cardBrand && (
                  <Typography variant="body2" color="text.secondary">
                    Bandeira: <strong>{card.payment.cardBrand}</strong>
                  </Typography>
                )}

                {Number.isFinite(card.payment.installments) && (
                  <Typography variant="body2" color="text.secondary">
                    Parcelamento:{" "}
                    <strong>
                      {card.payment.installments}x
                    </strong>
                  </Typography>
                )}

                {paymentAdj > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Acréscimo no cartão:{" "}
                    <strong>{money(paymentAdj)}</strong>
                  </Typography>
                )}

              </>
            )}
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ANTIFRAUDE */}
        {/* {!!card?.antifraud && (
          <>
            <SectionTitle icon={<ShieldRoundedIcon />} title="Antifraude" />
            <Typography variant="body2">
              Status:{" "}
              <strong>{card.antifraud.statusLabel || "—"}</strong>
            </Typography>
            {typeof card.antifraud.score === "number" && (
              <Typography variant="body2" color="text.secondary">
                Score: {card.antifraud.score}
              </Typography>
            )}
          </>
        )}

        <Divider sx={{ my: 2 }} /> */}

        {/* ENVIO */}
        {!!card?.shipping && (
          <>
            <SectionTitle
              title="Entrega"
              icon={<LocalShippingRoundedIcon />}
            />

            <Typography variant="body2">
              Método:{" "}
              <strong>{card.shipping.methodLabel || "—"}</strong>
            </Typography>
            
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {/* ENDEREÇO */}
        {!!addressLine && (
          <>
            <SectionTitle icon={<RoomRoundedIcon />} title="Endereço" />
            <Typography variant="body2">{addressLine}</Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <Box>
          {canCancel && !confirmCancel && (
            <Button color="error" onClick={() => setConfirmCancel(true)}>
              Cancelar Pedido
            </Button>
          )}
        </Box>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>

      <Dialog
        open={confirmCancel}
        onClose={() => !canceling && setConfirmCancel(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancelar pedido?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            O pedido será movido para a etapa de cancelamento configurada no canal.
            O estorno do pagamento, se houver, deve ser feito manualmente.
          </Typography>
          {cancelError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {cancelError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={canceling} onClick={() => setConfirmCancel(false)}>
            Voltar
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={canceling}
            onClick={handleCancelOrder}
          >
            {canceling ? "Cancelando…" : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default OrderDetailsDialog;
